import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../../Shared/interfaces/ApiError';
import useApiClient from '../../../../Shared/services/api';
import { useAppContext } from '../../../../Shared/store/appContext';
import { SHARED_CONSTANTS } from '../../../../Shared/constants/commonConstants';
import { EndpointQueryKeys } from '../../../constants/endpoints';
import {
  GetRolesApiResponse,
  Role,
  UpdateRoleResponse,
} from '../../../interfaces/RoleManagementInterfaces';
import { editRole, getRoles } from '../../../services/service';

export const useRoleManagement = () => {
  const { state } = useAppContext();
  const { t } = useTranslation();
  const api = useApiClient();
  const queryClient = useQueryClient();

  const [editRoleSuccessMessage, setEditRoleSuccessMessage] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const editRoleMutation = useMutation<UpdateRoleResponse, ApiError, Role>({
    mutationFn: (updatedRole: Role) =>
      editRole(api, state.selectedArtEventId, updatedRole),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EndpointQueryKeys.GET_ROLES, state.selectedArtEventId],
      });
      setEditRoleSuccessMessage(t('role-update-success-message'));
      setIsEditing(false);
      setSelectedRole(null);
      setTimeout(
        () => setEditRoleSuccessMessage(''),
        SHARED_CONSTANTS.SUCCESS_SNACKBAR_CLEAR_TIME
      );
    },
  });

  const rolesQuery = useQuery<GetRolesApiResponse, ApiError>({
    queryKey: [EndpointQueryKeys.GET_ROLES, state.selectedArtEventId],
    queryFn: () => getRoles(api, state.selectedArtEventId),
    enabled: true,
  });

  const handleEdit = (role: Role) => {
    editRoleMutation.reset();
    setEditRoleSuccessMessage('');
    setSelectedRole(role);
    setIsEditing(true);
  };

  const handleEditComplete = (updatedRole: Role) => {
    editRoleMutation.reset();
    editRoleMutation.mutate(updatedRole);
  };

  const handleCancel = () => {
    editRoleMutation.reset();
    setIsEditing(false);
    setSelectedRole(null);
  };

  return {
    rolesQuery,
    editProps: {
      isEditing,
      selectedRole,
      handleEdit,
      handleEditComplete,
      handleCancel,
      editRoleMutation,
    },
    snackbarProps: {
      editRoleSuccessMessage,
    },
    selectedArtEventId: state.selectedArtEventId,
  };
};
